import * as React from "react";
import {
  fetchCompanyNameById,
  fetchReadinessEmployeeByAuthUserId,
  getCurrentUser,
  signOut,
  updateReadinessEmployeeInfo,
} from "../api/queries";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  TextField,
  MenuItem,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LogoutIcon from "@mui/icons-material/Logout";
import { useI18n } from "../i18n/I18nProvider";
import { brand } from "../theme";
import type { ReadinessAdminForm, ReadinessEmployeeInfo, ReadinessStepId } from "../types/assessment";


//const ORDER: ReadinessStepId[] = ["adminInfo", "scoreInfo", "purposeInfo"];
const ORDER: ReadinessStepId[] = ["adminInfo", "scorepurposeInfo"];

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export default function ReadinessCheck(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const setLang: (l: "en" | "de") => void = i18n.setLang ?? (() => { });
  const t: (k: string) => string = i18n.t ?? ((k: string) => k);

  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const UI = React.useMemo(
    () => ({
      headerTitle: L("Readiness Level", "Bereitschaftsgrad"),
      headerSubtitle: L(
        "Resilience readiness diagnostic for employees.",
        "Resilienz-Readiness-Diagnostik für Mitarbeitende."
      ),

      progressTitleInfo: L("Before the assessment", "Vor dem Assessment"),
      progressStep: (i: number, total: number) =>
        L(`Step ${i} of ${total}`, `Schritt ${i} von ${total}`),

      leftPanelLoading: L("Loading...", "Lädt..."),
      leftPanelFail: L("Could not load your info", "Deine Infos konnten nicht geladen werden"),
      leftPanelFallback: L(
        "Failed to load employee info.",
        "Mitarbeiterdaten konnten nicht geladen werden."
      ),
      reload: L("Reload", "Neu laden"),
      logout: L("Log out", "Abmelden"),
      switchAccount: L("Switch account", "Konto wechseln"),

      btnBack: L("Previous", "Zurück"),
      btnContinue: L("Next", "Weiter"),
      btnSaving: L("Saving...", "Speichert..."),
      btnTakeAssessment: L("Take Assessment", "Assessment starten"),

      scoreIntroKicker: L(
        "Resilience Readiness",
        "Resilienz-Readiness"
      ),

      adminInfoTitle: L("Confirm your information", "Daten bestätigen"),
      adminInfoBody: L(
        "These fields are optional and are not scored, but they help make the assessment more relevant. If you participate anonymously, please do not enter your name.",
        "Diese Felder sind optional und werden nicht bewertet, helfen aber dabei, das Assessment relevanter zu machen. Wenn du anonym teilnimmst, gib bitte keinen Namen ein."
      ),
      q1FirstName: L("First name", "Vorname"),
      q1LastName: L("Last name", "Nachname"),
      q2Age: L("Age", "Alter"),
      q4Role: L("Position", "Position"),
      location: L("Location", "Standort"),
      gender: L("Gender", "Geschlecht"),
      genderPreferNot: L("Prefer not to say", "Keine Angabe"),
      genderMale: L("Male", "Männlich"),
      genderFemale: L("Female", "Weiblich"),
      genderOther: L("Other", "Andere"),

      scoreIntroTitle: L(
        "Your personal readiness level",
        "Dein persönlicher Bereitschaftsgrad"
      ),
      scoreIntroBody: L(
        "The readiness level translates personal resilience, practical readiness, role exposure, and continuity habits into one clear diagnostic. It is designed for employees who may need to stay capable during disruption while also protecting their household.",
        "Der Bereitschaftsgrad übersetzt persönliche Resilienz, praktische Vorbereitung, Rollenexposition und Kontinuitätsverhalten in eine klare Diagnose. Er wurde für Mitarbeitende entwickelt, die auch in Krisen handlungsfähig bleiben und gleichzeitig ihren Haushalt schützen wollen."
      ),
      scoreIntroTip1: L(
        "Understand your personal risk and readiness profile.",
        "Verstehe dein persönliches Risiko- und Bereitschaftsprofil."
      ),
      scoreIntroTip2: L(
        "Receive evidence-based module recommendations.",
        "Erhalte evidenzbasierte Modul-Empfehlungen."
      ),
      scoreIntroTip3: L(
        "Convert the result into a practical action plan.",
        "Verwandle das Ergebnis in einen konkreten Maßnahmenplan."
      ),

      infoTitle: L("Purpose of the program", "Zweck des Programms"),
      infoBody: L(
        "The program helps you prepare without overwhelm: it shows what matters first, what you already do well, and which small actions create the biggest resilience gain for work, home, and family.",
        "Das Programm hilft dir, dich ohne Überforderung vorzubereiten: Es zeigt, was zuerst zählt, was du bereits gut machst und welche kleinen Maßnahmen den größten Resilienzgewinn für Arbeit, Zuhause und Familie schaffen."
      ),
      tip1: L(
        "Less guesswork in crisis preparation.",
        "Weniger Rätselraten in der Krisenvorbereitung."
      ),
      tip2: L(
        "Better continuity for critical roles.",
        "Mehr Kontinuität für kritische Rollen."
      ),
      infoFooter: L(
        "A safer, calmer household plan.",
        "Ein sichererer und ruhigerer Haushaltsplan."
      ),

      errNotAuth: L(
        "Not authenticated. Please log in again.",
        "Nicht angemeldet. Bitte erneut einloggen."
      ),
      errEmpNotFound: L(
        "Employee record not found for this account.",
        "Kein Mitarbeiterprofil für dieses Konto gefunden."
      ),
      errSaveAdminInfo: L(
        "Failed to save employee information.",
        "Mitarbeiterdaten konnten nicht gespeichert werden."
      ),

      personalInfoTitle: L("Employee profile", "Mitarbeiterprofil"),
      personalInfoSubtitle: L(
        "Company and employee information.",
        "Unternehmens- und Mitarbeiterdaten."
      ),
      labelName: L("Name", "Name"),
      labelAge: L("Age", "Alter"),
      labelPosition: L("Position", "Position"),
      labelLocation: L("Location", "Standort"),
      labelGender: L("Gender", "Geschlecht"),
      labelCompany: L("Company", "Firma"),
      labelDepartment: L("Department", "Abteilung"),
    }),
    [L]
  );

  const [employee, setEmployee] = React.useState<ReadinessEmployeeInfo | null>(null);
  const [employeeLoading, setEmployeeLoading] = React.useState(true);
  const [employeeError, setEmployeeError] = React.useState<string | null>(null);
  const [idx, setIdx] = React.useState(0);
  const [savingAdminInfo, setSavingAdminInfo] = React.useState(false);

  const [adminForm, setAdminForm] = React.useState<ReadinessAdminForm>({
    first_name: "",
    last_name: "",
    age: "",
    position: "",
    location: "",
    gender: "",
  });

  React.useEffect(() => {
    let mounted = true;

    async function loadEmployee() {
      setEmployeeLoading(true);
      setEmployeeError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr || !userData?.user) throw new Error(UI.errNotAuth);

        const authUserId = userData.user.id;

        const { data: empRow, error: empErr } = await fetchReadinessEmployeeByAuthUserId(authUserId);

        if (empErr) throw empErr;
        if (!empRow) throw new Error(UI.errEmpNotFound);

        const { data: compRow, error: compErr } = await fetchCompanyNameById(empRow.company_id);

        if (compErr) throw compErr;

        const genderValue =
          empRow.gender === "male" || empRow.gender === "female" || empRow.gender === "other"
            ? empRow.gender
            : "";

        const emp: ReadinessEmployeeInfo = {
          id: empRow.id,
          first_name: empRow.first_name ?? "",
          last_name: empRow.last_name ?? "",
          age: empRow.age ?? null,
          position: empRow.position ?? "",
          location: empRow.location ?? "",
          gender: genderValue,
          company_role: empRow.company_role ?? "(No role yet)",
          department: empRow.department ?? "(No department set)",
          company_email: empRow.company_email ?? "(No email set)",
          company: compRow?.name ?? "(No company set)",
        };

        if (!mounted) return;

        setEmployee(emp);
        setAdminForm({
          first_name: emp.first_name,
          last_name: emp.last_name,
          age: emp.age !== null ? String(emp.age) : "",
          position: emp.position,
          location: emp.location,
          gender: emp.gender,
        });
      } catch (e: any) {
        if (!mounted) return;
        setEmployee(null);
        setEmployeeError(e?.message ?? UI.leftPanelFallback);
      } finally {
        if (!mounted) return;
        setEmployeeLoading(false);
      }
    }

    loadEmployee();

    return () => {
      mounted = false;
    };
  }, [UI.errNotAuth, UI.errEmpNotFound, UI.leftPanelFallback]);

  const handleLogout = async () => {
    // Signs out whatever local session is stuck here -- e.g. the account
    // was deleted after this browser last logged in, so every fetch here
    // fails and just reloading loops on the same stale, unusable session.
    await signOut();
    localStorage.removeItem("tk.currentPage");
    localStorage.removeItem("tk.nav.role");
    handlePageChange("Entry");
  };

  const step = ORDER[idx];
  const progress = clamp(((idx + 1) / ORDER.length) * 100, 0, 100);

  const saveAdminInfo = async () => {
    if (!employee) return;

    setSavingAdminInfo(true);
    setEmployeeError(null);

    const rawAge = adminForm.age.trim();
    const parsedAge = rawAge === "" ? null : Number.parseInt(rawAge, 10);
    const ageValue = parsedAge !== null && Number.isFinite(parsedAge) ? parsedAge : null;

    const genderValue =
      adminForm.gender === "male" || adminForm.gender === "female" || adminForm.gender === "other"
        ? adminForm.gender
        : null;

    const nextEmployee = {
      first_name: adminForm.first_name.trim() || null,
      last_name: adminForm.last_name.trim() || null,
      age: ageValue,
      position: adminForm.position.trim() || null,
      location: adminForm.location.trim() || null,
      gender: genderValue,
    };

    const { error } = await updateReadinessEmployeeInfo(employee.id, nextEmployee);

    setSavingAdminInfo(false);

    if (error) throw error;

    setEmployee({
      ...employee,
      first_name: nextEmployee.first_name ?? "",
      last_name: nextEmployee.last_name ?? "",
      age: nextEmployee.age,
      position: nextEmployee.position ?? "",
      location: nextEmployee.location ?? "",
      gender: nextEmployee.gender ?? "",
    });
  };

  const goNext = async () => {
    if (step === "adminInfo") {
      try {
        await saveAdminInfo();
        setIdx((v) => Math.min(v + 1, ORDER.length - 1));
      } catch (e: any) {
        setEmployeeError(e?.message ?? UI.errSaveAdminInfo);
      }
      return;
    }

    //JZ if (step === "purposeInfo") {
    if (step === "scorepurposeInfo") {
      handlePageChange("OccasionSelection");
      return;
    }

    setIdx((v) => Math.min(v + 1, ORDER.length - 1));
  };

  const goBack = () => {
    setIdx((v) => Math.max(v - 1, 0));
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                bgcolor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                display: "grid",
                placeItems: "center",
                color: "#111827",
                flexShrink: 0,
              }}
            >
              <TuneOutlinedIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {UI.headerTitle}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {UI.headerSubtitle}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={lang}
              onChange={(_, next) => {
                if (next) setLang(next);
              }}
              sx={{
                bgcolor: "#FFFFFFB3",
                border: "1px solid #00000014",
                borderRadius: 999,
                overflow: "hidden",
                "& .MuiToggleButton-root": {
                  px: 1.1,
                  py: 0.45,
                  fontWeight: 900,
                  textTransform: "none",
                  border: 0,
                  color: "#111827B3",
                },
                "& .Mui-selected": {
                  color: "#111827",
                  bgcolor: "#11182712",
                },
              }}
            >
              <ToggleButton value="en">{t("lang.en")}</ToggleButton>
              <ToggleButton value="de">{t("lang.de")}</ToggleButton>
            </ToggleButtonGroup>

            <Button
              size="small"
              startIcon={<LogoutIcon fontSize="small" />}
              onClick={handleLogout}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                color: "#374151",
                bgcolor: "#FFFFFFB3",
                border: "1px solid #00000014",
                borderRadius: 999,
                px: 1.25,
              }}
            >
              {UI.switchAccount}
            </Button>
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
                <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                  {UI.progressTitleInfo}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 800 }}>
                  {UI.progressStep(idx + 1, ORDER.length)}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 20,
                  bgcolor: "#E5E7EB",
                  "& .MuiLinearProgress-bar": { borderRadius: 20, bgcolor: "#111827" },
                }}
              />
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "420px 1fr" },
                gap: 2.5,
              }}
            >
              <Stack spacing={2}>
                {employee ? (
                  <InfoPanel employee={employee} UI={UI} />
                ) : (
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      bgcolor: "#FFFFFF",
                      p: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                      {employeeLoading ? UI.leftPanelLoading : UI.leftPanelFail}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.75 }}>
                      {employeeError ?? UI.leftPanelFallback}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button
                        variant="outlined"
                        sx={{ textTransform: "none", fontWeight: 900 }}
                        onClick={() => window.location.reload()}
                      >
                        {UI.reload}
                      </Button>
                      <Button
                        variant="text"
                        sx={{ textTransform: "none", fontWeight: 900 }}
                        onClick={handleLogout}
                      >
                        {UI.logout}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Stack>

              <Stack spacing={2}>
                  {step === "adminInfo" ? (
                    <AdminInfoStep UI={UI} form={adminForm} setForm={setAdminForm} />
                  ) : step === "scorepurposeInfo" ? (
                    <>
                    <ScoreInfoStep UI={UI} />
                    <InfoStep UI={UI} />
                    </>
                  ):(<></>)}
                  {/*

                    JZ ) : step === "scoreInfo" ? (    
                      <InfoStep UI={UI} />
                  ) : (
                    <InfoStep UI={UI} />
                  )}
                  */}

                {employeeError && (
                  <Typography variant="body2" sx={{ color: "#B91C1C", fontWeight: 800 }}>
                    {employeeError}
                  </Typography>
                )}

                <Stack direction="row" spacing={1.25} justifyContent="space-between">
                  <Button
                    variant="outlined"
                    onClick={goBack}
                    disabled={idx === 0 || savingAdminInfo}
                    startIcon={<ArrowBackIcon />}
                    sx={btnOutlineSx}
                  >
                    {UI.btnBack}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={goNext}
                    disabled={savingAdminInfo || employeeLoading || !employee}
                    endIcon={<ArrowForwardIcon />}
                    sx={btnPrimarySx}
                  >
                    {savingAdminInfo
                      ? UI.btnSaving
                      //JZ : step === "scorepurposeInfo"
                      : step === "scorepurposeInfo"
                        ? UI.btnTakeAssessment
                        : UI.btnContinue}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function AdminInfoStep({
  UI,
  form,
  setForm,
}: {
  UI: any;
  form: ReadinessAdminForm;
  setForm: React.Dispatch<React.SetStateAction<ReadinessAdminForm>>;
}) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid #E5E7EB",
        bgcolor: "#FFFFFF",
        p: 2,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: brand.accentStrong,
          mb: 2,
        }}
      >
        {UI.scoreIntroKicker}
      </Typography>

      <Typography sx={{ fontWeight: 900, fontSize: 34, color: "#111827", lineHeight: 1.1 }}>
        {UI.adminInfoTitle}
      </Typography>

      <Typography variant="body1" sx={{ color: "#6B7280", mt: 2, mb: 2.5, lineHeight: 1.7 }}>
        {UI.adminInfoBody}
      </Typography>

      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            fullWidth
            label={UI.q1FirstName}
            value={form.first_name}
            onChange={(e) => setForm((v) => ({ ...v, first_name: e.target.value }))}
          />

          <TextField
            fullWidth
            label={UI.q1LastName}
            value={form.last_name}
            onChange={(e) => setForm((v) => ({ ...v, last_name: e.target.value }))}
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            fullWidth
            label={UI.q2Age}
            type="number"
            value={form.age}
            onChange={(e) => setForm((v) => ({ ...v, age: e.target.value }))}
          />

          <TextField
            fullWidth
            select
            label={UI.gender}
            value={form.gender}
            onChange={(e) =>
              setForm((v) => ({
                ...v,
                gender: e.target.value as ReadinessAdminForm["gender"],
              }))
            }
          >
            <MenuItem value="">{UI.genderPreferNot}</MenuItem>
            <MenuItem value="male">{UI.genderMale}</MenuItem>
            <MenuItem value="female">{UI.genderFemale}</MenuItem>
            <MenuItem value="other">{UI.genderOther}</MenuItem>
          </TextField>
        </Stack>

        <TextField
          fullWidth
          label={UI.q4Role}
          value={form.position}
          onChange={(e) => setForm((v) => ({ ...v, position: e.target.value }))}
        />

        <TextField
          fullWidth
          label={UI.location}
          value={form.location}
          onChange={(e) => setForm((v) => ({ ...v, location: e.target.value }))}
        />
      </Stack>
    </Box>
  );
}

function ScoreInfoStep({ UI }: { UI: any }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid #E5E7EB",
        bgcolor: "#FFFFFF",
        p: 2,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: brand.accentStrong,
          mb: 2,
        }}
      >
        {UI.scoreIntroKicker}
      </Typography>

      <Typography sx={{ fontWeight: 900, fontSize: 36, color: "#111827", lineHeight: 1.1 }}>
        {UI.scoreIntroTitle}
      </Typography>

      <Typography variant="body1" sx={{ color: "#6B7280", mt: 2, lineHeight: 1.7 }}>
        {UI.scoreIntroBody}
      </Typography>

      <Box component="ul" sx={{ mt: 2, pl: 2.4, color: "#111827" }}>
        <li>
          <Typography sx={{ fontWeight: 800 }}>{UI.scoreIntroTip1}</Typography>
        </li>
        <li>
          <Typography sx={{ fontWeight: 800 }}>{UI.scoreIntroTip2}</Typography>
        </li>
        <li>
          <Typography sx={{ fontWeight: 800 }}>{UI.scoreIntroTip3}</Typography>
        </li>
      </Box>
    </Box>
  );
}

function InfoStep({ UI }: { UI: any }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid #E5E7EB",
        bgcolor: "#FFFFFF",
        p: 2,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: brand.accentStrong,
          mb: 2,
        }}
      >
        {UI.scoreIntroKicker}
      </Typography>

      <Typography sx={{ fontWeight: 900, fontSize: 36, color: "#111827", lineHeight: 1.1 }}>
        {UI.infoTitle}
      </Typography>

      <Typography variant="body1" sx={{ color: "#6B7280", mt: 2, lineHeight: 1.7 }}>
        {UI.infoBody}
      </Typography>

      <Box component="ul" sx={{ mt: 2, pl: 2.4, color: "#111827" }}>
        <li>
          <Typography sx={{ fontWeight: 800 }}>{UI.tip1}</Typography>
        </li>
        <li>
          <Typography sx={{ fontWeight: 800 }}>{UI.tip2}</Typography>
        </li>
        <li>
          <Typography sx={{ fontWeight: 800 }}>{UI.infoFooter}</Typography>
        </li>
      </Box>
    </Box>
  );
}

function InfoPanel({ employee, UI }: { employee: ReadinessEmployeeInfo; UI: any }) {
  const fullName = `${employee.first_name} ${employee.last_name}`.trim() || "—";
  const genderLabel =
    employee.gender === "male"
      ? UI.genderMale
      : employee.gender === "female"
        ? UI.genderFemale
        : employee.gender === "other"
          ? UI.genderOther
          : UI.genderPreferNot;

  return (
    <Box sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", overflow: "hidden" }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.25,
              bgcolor: "#F3F4F6",
              border: "1px solid #E5E7EB",
              display: "grid",
              placeItems: "center",
              color: "#111827",
              flexShrink: 0,
            }}
          >
            <PersonOutlineIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, color: "#111827" }}>{UI.personalInfoTitle}</Typography>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {UI.personalInfoSubtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label={UI.labelName} value={fullName} />
          <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label={UI.labelAge} value={employee.age !== null ? String(employee.age) : "—"} />
          <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label={UI.labelGender} value={genderLabel} />
          <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label={UI.labelPosition} value={employee.position || "—"} />
          <InfoRow icon={<ApartmentOutlinedIcon fontSize="small" />} label={UI.labelLocation} value={employee.location || "—"} />
          <InfoRow icon={<ApartmentOutlinedIcon fontSize="small" />} label={UI.labelCompany} value={employee.company} />
          <InfoRow icon={<ApartmentOutlinedIcon fontSize="small" />} label={UI.labelDepartment} value={employee.department} />
        </Stack>
      </Box>
    </Box>
  );
}

function InfoRow(props: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Box sx={{ borderRadius: 2.5, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", px: 1.5, py: 1.15 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ color: "#6B7280", display: "grid", placeItems: "center" }}>{props.icon}</Box>
          <Typography sx={{ fontWeight: 800, color: "#6B7280" }}>{props.label}</Typography>
        </Stack>

        <Typography
          sx={{
            fontWeight: 900,
            color: "#111827",
            textAlign: "right",
            maxWidth: "58%",
            overflowWrap: "anywhere",
          }}
        >
          {props.value}
        </Typography>
      </Stack>
    </Box>
  );
}

const btnPrimarySx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  bgcolor: "#111827",
  boxShadow: "none",
  px: 2.2,
  py: 1.1,
  "&:hover": { bgcolor: "#0B1220", boxShadow: "none" },
} as const;

const btnOutlineSx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "#D1D5DB",
  color: "#111827",
  bgcolor: "#FFFFFF",
  px: 2.2,
  py: 1.1,
  "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
} as const;
